import { lighten } from '@mui/material';
import { DataGrid, GridRowIdGetter, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { theme } from '../theme';
import TableActions from './TableActions';

interface DataTableGridProps {
  rows: any[];
  columns: any[];
  columnVisibilityModel?: any;
  handleSelectionChange?: (newSelection: GridRowSelectionModel) => void | undefined;
  rowSelectionModel?: GridRowSelectionModel | undefined;
  itemsSelected?: boolean;
  newOnClick?: () => void | null;
  newLabel?: string | null;
  newDisabled?: boolean;
  extraBtnOnClick?: () => void;
  extraBtnLabel?: string;
  extraBtnDisabled?: boolean;
  getRowId?: GridRowIdGetter<any>;
  refreshData?: () => void | null;
  isRefetching?: boolean;
  refreshDisabled?: boolean;
  menuItems?:
    | {
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
        hasDivider?: boolean;
        disabled?: boolean;
      }[]
    | null;
  secondarySlot?: React.ReactNode;
  pagination?: boolean;
  pageSize?: number;
  onRowClick?: (params: GridRowParams) => void;
  slots?: boolean;
  disableColumnResize?: boolean;
  hideSearchBar?: boolean;
  processRowUpdate?: (newRow: any, oldRow: any) => Promise<any> | any;
  onProcessRowUpdateError?: (error: any) => void;
}

const CustomToolbar = (props: any) => (
  <TableActions
    itemsSelected={props.itemsSelected}
    btnLabel={props.btnLabel}
    btnOnclick={props.btnOnclick}
    btnDisabled={props.btnDisabled}
    refreshData={props.refreshData}
    isRefetching={props.isRefetching}
    refreshDisabled={props.refreshDisabled}
    menuItems={props.menuItems}
    secondarySlot={props.secondarySlot}
    extraButtonLabel={props.extraButtonLabel}
    extraButtonOnClick={props.extraButtonOnClick}
    extraButtonDisabled={props.extraButtonDisabled}
    hideSearchBar={props.hideSearchBar}
  />
);

const DataTableGrid = ({
  rows,
  columns,
  columnVisibilityModel = {},
  handleSelectionChange,
  rowSelectionModel,
  itemsSelected,
  newOnClick,
  newLabel,
  newDisabled = false,
  extraBtnOnClick,
  extraBtnLabel,
  extraBtnDisabled,
  refreshData,
  isRefetching,
  refreshDisabled = false,
  menuItems,
  secondarySlot,
  pagination = false,
  pageSize = 10,
  onRowClick,
  slots,
  disableColumnResize = true,
  hideSearchBar = false,
  processRowUpdate,
  onProcessRowUpdateError,
}: DataTableGridProps) => {

  const defaultPageSize = pageSize;
  const savedPageSize = pagination
    ? Number(localStorage.getItem('dataTablePageSize')) || defaultPageSize
    : defaultPageSize;

  return (disableColumnResize ?
    <DataGrid
      rows={rows}
      columns={columns}
      disableColumnResize
      hideFooter={!pagination}
      checkboxSelection={handleSelectionChange !== undefined}
      onRowSelectionModelChange={handleSelectionChange}
      rowSelectionModel={rowSelectionModel}
      slots={slots ? undefined : { toolbar: CustomToolbar }}
      slotProps={{
        toolbar: {
          itemsSelected,
          btnOnclick: newOnClick,
          btnLabel: newLabel,
          btnDisabled: newDisabled,
          refreshData,
          isRefetching,
          refreshDisabled,
          menuItems,
          secondarySlot,
          extraButtonLabel: extraBtnLabel,
          extraButtonOnClick: extraBtnOnClick,
          extraButtonDisabled: extraBtnDisabled,
          hideSearchBar: hideSearchBar,
        } as any,
      }}
      onRowClick={onRowClick}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={onProcessRowUpdateError}
      localeText={{ noRowsLabel: 'No data' }}
      columnVisibilityModel={columnVisibilityModel}
      sx={{
        border: 'none',
        '& .MuiDataGrid-row': {
          cursor: onRowClick ? 'pointer' : 'default',
        },
        '& .MuiDataGrid-row:nth-of-type(odd)': {
          backgroundColor: lighten(theme.palette.primary.main, 0.95),
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: lighten(theme.palette.primary.main, 0.9),
        },
        '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row:hover.Mui-selected': {
          backgroundColor: lighten(theme.palette.primary.main, 0.8),
        },
      }}
      initialState={
        pagination
          ? {
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: savedPageSize,
                },
              },
            }
          : undefined
      }
      pageSizeOptions={pagination ? [10, 25, 50] : undefined}
      onPaginationModelChange={(model) => {
        localStorage.setItem('dataTablePageSize', model.pageSize.toString());
      }}
    /> : <DataGrid
      rows={rows}
      columns={columns}
      hideFooter={!pagination}
      checkboxSelection={handleSelectionChange !== undefined}
      onRowSelectionModelChange={handleSelectionChange}
      rowSelectionModel={rowSelectionModel}
      slots={slots ? undefined : { toolbar: CustomToolbar }}
      slotProps={{
        toolbar: {
          itemsSelected,
          btnOnclick: newOnClick,
          btnLabel: newLabel,
          btnDisabled: newDisabled,
          refreshData,
          isRefetching,
          refreshDisabled,
          menuItems,
          secondarySlot,
        } as any,
      }}
      onRowClick={onRowClick}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={onProcessRowUpdateError}
      localeText={{ noRowsLabel: 'No data' }}
      columnVisibilityModel={columnVisibilityModel}
      sx={{
        border: 'none',
        '& .MuiDataGrid-row': {
          cursor: onRowClick ? 'pointer' : 'default',
        },
        '& .MuiDataGrid-row:nth-of-type(odd)': {
          backgroundColor: lighten(theme.palette.primary.main, 0.95),
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: lighten(theme.palette.primary.main, 0.9),
        },
        '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row:hover.Mui-selected': {
          backgroundColor: lighten(theme.palette.primary.main, 0.8),
        },
      }}
      initialState={
        pagination
          ? {
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: savedPageSize,
                },
              },
            }
          : undefined
      }
      pageSizeOptions={pagination ? [10, 25, 50] : undefined}
      onPaginationModelChange={(model) => {
        localStorage.setItem('dataTablePageSize', model.pageSize.toString());
      }}
    />
  );
};

export default DataTableGrid;
